import React from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { TextArea } from "src/components/TextArea/TextArea.tsx";
import type { TextareaProps } from "src/components/TextArea/TextArea.types.ts";
import { composeRefs } from "@exonn/hooks";

export type TextAreaFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
} & TextareaProps &
  Omit<ControllerProps<TFieldValues, TName>, "render">;

// todo: forward ref
export const TextAreaField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: TextAreaFieldProps<TFieldValues, TName>,
) => {
  const {
    name,
    control,
    disabled,
    rules,
    shouldUnregister,
    defaultValue,
    onChange: onChangeProp,
    onBlur: onBlurProp,
    ...restProps
  } = props;

  return (
    <Controller
      control={control}
      rules={rules}
      shouldUnregister={shouldUnregister}
      key={name}
      defaultValue={defaultValue}
      disabled={disabled}
      render={({
        field: { name, disabled, value, onChange, onBlur, ref },
        formState: { errors },
      }) => {
        return (
          <TextArea
            ref={composeRefs(ref)}
            disabled={disabled}
            onChange={(e) => {
              onChange(e.target.value);
              onChangeProp?.(e);
            }}
            onBlur={(event) => {
              onBlur();
              onBlurProp?.(event);
            }}
            error={errors?.root?.[name]?.message}
            value={value}
            {...restProps}
          />
        );
      }}
      name={name}
    />
  );
};
